import { _ } from "../../../polyglot/polyglot.js";
import {
	percentIcon,
	plusIcon,
	trashAltIcon,
} from "../../../elements/icons/index.js";
import AbstractWidgetsListStrategy from "../../../widgets/WidgetsListWidget/strategies/AbstractWidgetsListStrategy.js";

/**
 * Strategy to render accounts payable and receivable delays inside a
 * WidgetsListWidget.
 * It acts on an array of integers, and trigger an event when the values
 * changed and are valid.
 *
 * The validation here is that the sum of all the integers is exactly equals to
 * `100`.
 *
 * @param {function} [onValuesChange] - Function invoked when a value changed,
 *   or a value is added or removed
 */
export default class AccountPayableAndReceivableDelayStrategy extends AbstractWidgetsListStrategy {
	constructor({ onValuesChange = () => {} } = {}) {
		super(...arguments);
		this._onValuesChange = onValuesChange;
	}

	extraCSSClasses() {
		return "AccountPayableAndReceivableDelayEditorWidget";
	}

	isValidatingOnChange() {
		return true;
	}

	canDeleteItem({ index }) {
		const totalLength = this._getItemsOrEmpty().length;
		return totalLength !== 1 && index === totalLength - 1;
	}

	isInlined() {
		return true;
	}

	addNewItem(...args) {
		super.addNewItem(...args);
		this._checkValues();
	}

	deleteItem(...args) {
		super.deleteItem(...args);
		this._checkValues();
	}

	itemRenderer({ item: value, index }) {
		const resolvedIndex = index + 1;
		const label =
			resolvedIndex === 1
				? _("Paid in the current month")
				: _("Paid in month {index}", { index: resolvedIndex });

		return (html) =>
			html.div(
				html.div(
					{ class: "percentage-label" },
					html.label({ "data-test": "item-label" }, label),
				),
				html.div(
					{ class: "percentage-input" },
					html.input({
						"data-test": "item",
						class: "form-control",
						type: "number",
						name: `month-${index}`,
						value: value.toString(),
						change: ({ target }) => {
							this._items[index] = +target.value;
							this._checkValues();
						},
					}),
					html.span({ class: "percent-container" }, percentIcon),
				),
			);
	}

	addButtonRenderer({ onClick }) {
		return (html) =>
			html.div(
				{
					class: "btn btn-default",
					click: onClick,
					"data-test": "add",
				},
				plusIcon,
			);
	}

	deleteButtonRenderer({ onClick }) {
		return (html) => {
			html.button(
				{
					class: "btn btn-xs delete-button",
					click: onClick,
					"data-test": "delete",
				},
				trashAltIcon,
			);
		};
	}

	_checkValues() {
		this._triggerOnChange();

		if (!this.isValid()) {
			return;
		}

		this._onValuesChange(this._items);
	}

	_makeNewItem() {
		return 0;
	}

	_validate({ collector }) {
		const sumOfValues = this._getItemsOrEmpty().reduce(
			(result, currentValue) => result + currentValue,
			0,
		);

		collector.addErrorIf(
			sumOfValues !== 100,
			_("All inputs should add up to 100"),
		);
	}
}
